/* eslint-disable no-console */
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors} from '../../constants/Common';
import {
  applyCADocumentTypeAutoDefaults,
  CADocumentTypeOptions,
  getVisibleCADocumentTypeSelection,
  getVisibleMethodOptions
} from '../../pages/aiDocumentManager/Constants/commonConstants';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import DocumentUploadPreview from '../DocumentUploadPreview/DocumentUploadPreview';

import {
  AIDocumentManagerKey,
  AIDocumentManagerLabel
} from './Constants/IP/AIDocumentManagerLabel';
import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {filterSelectOptions} from './Utils/commonFunctions';

const CADocumentManagerForm: React.FC<{
  currentStep?: number;
}> = ({currentStep}) => {
  const FormInstance = useFormInstance();
  const [searchParam] = useSearchParams();

  const yearFilter = yearSelect();
  const resolvedDocType = applyCADocumentTypeAutoDefaults(
    getVisibleCADocumentTypeSelection(searchParam.get('doc_type') ?? '')
  );

  useEffect(() => {
    if (resolvedDocType.doc_type) {
      FormInstance.setFieldsValue(resolvedDocType);
    }
  }, []);

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={AIDocumentManagerKey.YEAR}
              label={AIDocumentManagerLabel.YEAR}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(AIDocumentManagerLabel.YEAR)
                }
              ]}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                onChange={() => {
                  FormInstance.setFieldsValue({
                    [AIDocumentManagerKey.FACILITY_TYPE]: undefined
                  });
                }}
                suffixIcon={<YearCalender />}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[AIDocumentManagerKey.YEAR]} noStyle>
              {({getFieldValue}) => {
                const selectedYear = getFieldValue(AIDocumentManagerKey.YEAR);

                const facilityDetails =
                  useCompanyStore
                    .getState()
                    .facilityDetails?.[selectedYear]?.map((item: any) => {
                      return {
                        isLeased: item.isLeased,
                        country: item.country,
                        type: item.type,
                        label: item.name,
                        value: item._id
                      };
                    }) ?? [];

                const facilityIdFromParam = searchParam.get('facilityId');
                const facilityIdExists = facilityDetails.some(
                  (option: any) => option.value === facilityIdFromParam
                );

                const initialFacilityValue = facilityIdExists
                  ? facilityIdFromParam
                  : undefined;

                return (
                  <Form.Item
                    name={AIDocumentManagerKey.FACILITY_TYPE}
                    label={AIDocumentManagerLabel.FACILITY_TYPE}
                    labelCol={{span: 24}}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(
                          AIDocumentManagerLabel.FACILITY_TYPE
                        )
                      }
                    ]}
                    initialValue={initialFacilityValue}
                    validateFirst
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Facility Name"
                      defaultValue={undefined}
                      options={facilityDetails}
                      popupMatchSelectWidth={false}
                      size="middle"
                      filterOption={filterSelectOptions}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={AIDocumentManagerKey.DOC_TYPE}
              label={AIDocumentManagerLabel.BILL_TYPE}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(
                    AIDocumentManagerLabel.BILL_TYPE
                  )
                }
              ]}
              initialValue={resolvedDocType.doc_type}
              validateFirst
            >
              <Select
                showSearch
                allowClear
                placeholder={AIDocumentManagerLabel.BILL_TYPE}
                defaultValue={undefined}
                options={CADocumentTypeOptions.filter((item) => !item?.hidden)}
                popupMatchSelectWidth={false}
                size="middle"
                onChange={(docType) => {
                  const autoDefaults = applyCADocumentTypeAutoDefaults({
                    doc_type: docType
                  });
                  FormInstance.setFieldsValue({
                    [AIDocumentManagerKey.BILL_TYPE]: autoDefaults.bill_type,
                    [AIDocumentManagerKey.METHOD]: autoDefaults.method
                  });
                }}
                filterOption={filterSelectOptions}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev[AIDocumentManagerKey.DOC_TYPE] !==
                curr[AIDocumentManagerKey.DOC_TYPE]
              }
              noStyle
            >
              {({getFieldValue}) => {
                const docType = getFieldValue(AIDocumentManagerKey.DOC_TYPE);

                const subDocTypes = CADocumentTypeOptions.filter(
                  (item) => item.value === docType && item?.children
                );

                return (
                  subDocTypes?.[0] && (
                    <Form.Item
                      name={AIDocumentManagerKey.BILL_TYPE}
                      label={AIDocumentManagerLabel.SUB_BILL_TYPE}
                      labelCol={{span: 24}}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            AIDocumentManagerLabel.BILL_TYPE
                          )
                        }
                      ]}
                      initialValue={resolvedDocType.bill_type}
                      validateFirst
                    >
                      <Select
                        className="select"
                        placeholder={`Choose ${AIDocumentManagerLabel.SUB_BILL_TYPE}`}
                        options={(subDocTypes?.[0].children as any[])?.filter(
                          (item: any) => !item?.hidden
                        )}
                        onChange={(billType) => {
                          const docType = FormInstance.getFieldValue(
                            AIDocumentManagerKey.DOC_TYPE
                          );
                          const methodOptions = getVisibleMethodOptions(
                            docType,
                            billType
                          );
                          FormInstance.setFieldValue(
                            AIDocumentManagerKey.METHOD,
                            methodOptions.length === 1
                              ? methodOptions[0].value
                              : undefined
                          );
                        }}
                        disabled={!subDocTypes}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev[AIDocumentManagerKey.BILL_TYPE] !==
                curr[AIDocumentManagerKey.BILL_TYPE]
              }
              noStyle
            >
              {({getFieldValue}) => {
                const docType = getFieldValue(AIDocumentManagerKey.DOC_TYPE);
                const billType = getFieldValue(AIDocumentManagerKey.BILL_TYPE);

                const subDocTypes: any = CADocumentTypeOptions.filter(
                  (item) => item.value === docType && item?.children
                )?.[0]?.children;

                const methodTypes = subDocTypes?.filter(
                  (item: any) => item.value === billType
                )?.[0]?.children;

                return (
                  methodTypes?.[0] && (
                    <Form.Item
                      name={AIDocumentManagerKey.METHOD}
                      label={AIDocumentManagerLabel.METHOD}
                      labelCol={{span: 24}}
                      rules={[
                        {
                          required: true,
                          message: CommonErrors.mandatory(
                            AIDocumentManagerLabel.METHOD
                          )
                        }
                      ]}
                      initialValue={resolvedDocType.method}
                      validateFirst
                    >
                      <Select
                        className="select"
                        placeholder={`Choose ${AIDocumentManagerLabel.METHOD}`}
                        options={methodTypes ?? []}
                        disabled={!methodTypes}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
            <Form.Item name={'type'} initialValue={'CA'}></Form.Item>
          </Col>
        </>
      )}
      {currentStep === 1 && (
        <>
          <Col className="gutter-row">
            <Form.Item noStyle>
              <DocumentUploadPreview
                formInstance={FormInstance}
                validationMessage="Please upload bill"
              />
            </Form.Item>
            <Form.Item name="file_name" hidden />
            <Form.Item name="pollingStatus" hidden />
          </Col>
        </>
      )}
      {currentStep === 2 && <Col className="gutter-row"></Col>}
    </>
  );
};

export default CADocumentManagerForm;
